import { useState, useCallback, useEffect, useMemo, useRef } from "react";
import ax from "../../config/axios";
import { IRRBarChart } from "./IRRBarChart";
import { IRRLatestTable } from "./IRRLatestTable";
import { IRRRolloverChart } from "./IRRRolloverChart";
import { IRRStackedChart } from "./IRRStackedChart";
import type { IrrData, IrrDataPoint } from "./types";
import { getIrrValue, getRootsExceedingRange } from "./utils";
import styles from "./IRRAnalysis.module.css";

export default function IRRAnalysis() {
  const [irrApiData, setIrrApiData] = useState<IrrData>({});
  const [excludedRoots, setExcludedRoots] = useState<string[]>([]);
  const [excludeInput, setExcludeInput] = useState("");
  const [minIrr, setMinIrr] = useState(0);
  const [maxIrr, setMaxIrr] = useState(1);
  const [selectedRolloverRoot, setSelectedRolloverRoot] = useState<string | null>(null);
  const [rootsExceedingRange, setRootsExceedingRange] = useState<string[]>([]);
  const [sofrAdjusted, setSofrAdjusted] = useState(false);
  const [sliderDateIndex, setSliderDateIndex] = useState(0);
  const [lowerChartMode, setLowerChartMode] = useState<"stacked" | "historical">("historical");
  const [stackedSofrAdjusted, setStackedSofrAdjusted] = useState(false);
  const hasInitializedSlider = useRef(false);
  const hasInitializedRolloverRoot = useRef(false);
  const prevFilteredCountRef = useRef(0);
  const [irrBoundsReady, setIrrBoundsReady] = useState(false);
  const [lastUpdate, setLastUpdate] = useState<string | null>(null);
  // main page api call
  useEffect(() => {
    ax.get("/data/irr-data")
      .then((response) => {
        setIrrApiData(response.data.data);
        setLastUpdate(response.data.last_update);
      })
      .catch((error) => {
        console.error("Error fetching IRR data:", error);
      });
  }, []);

  /** Derived from irrApiData: flat structure per root with active spread data. */
  const { irrData, dates, roots } = useMemo(() => {
    const irrData: IrrDataPoint[] = Object.entries(irrApiData).map(([root, d]) => {
      const activeData = d.data[d.active_spread] ?? [];
      return {
        root,
        displayName: d.name ?? root,
        data: activeData.map((p) => ({ ...p, dateMs: new Date(p.date).getTime() })),
      };
    });
    const dates = [...new Set(irrData.flatMap((d) => d.data.map((p) => p.date)))].sort();
    const roots = irrData.map((d) => d.root);
    return { irrData, dates, roots };
  }, [irrApiData]);

  // Recalculate min/max IRR from included data when API loads, sofrAdjusted, or excludedRoots changes.
  // When roots are excluded: only overwrite if the new range would be tighter (higher min or lower max).
  useEffect(() => {
    const included = irrData.filter((d) => !excludedRoots.includes(d.root));
    const allIrr = included.flatMap((d) => d.data.map((p) => getIrrValue(p, sofrAdjusted)));
    if (allIrr.length > 0) {
      const newMin = Math.min(...allIrr);
      const newMax = Math.max(...allIrr);
      if (excludedRoots.length === 0) {
        setMinIrr(newMin);
        setMaxIrr(newMax);
      } else {
        setMinIrr((prev) => (newMin > prev ? newMin : prev));
        setMaxIrr((prev) => (newMax < prev ? newMax : prev));
      }
      setIrrBoundsReady(true);
    }
  }, [irrData, sofrAdjusted, excludedRoots]);

  // When min/max IRR change, compute roots that exceed the range for any date
  useEffect(() => {
    if (!irrBoundsReady) return;
    const exceeding = getRootsExceedingRange(irrData, minIrr, maxIrr, excludedRoots, sofrAdjusted);
    setRootsExceedingRange(exceeding);
  }, [irrData, minIrr, maxIrr, excludedRoots, sofrAdjusted, irrBoundsReady]);

  // Set slider to end only on initial data load. Afterwards, preserve user's position when plot-configuring
  // state changes (sofrAdjusted, excludedRoots, min/max IRR, etc.). The ref ensures we only run once.
  useEffect(() => {
    if (dates.length > 0 && !hasInitializedSlider.current) {
      hasInitializedSlider.current = true;
      setSliderDateIndex(dates.length - 1);
    }
  }, [dates.length]);

  // Default rollover root to first item when data loads
  useEffect(() => {
    if (roots.length > 0 && !hasInitializedRolloverRoot.current) {
      hasInitializedRolloverRoot.current = true;
      setSelectedRolloverRoot(roots[0]);
    }
  }, [roots]);

  // Clamp slider when dates shrink (e.g. refetch with fewer dates) so index stays valid
  useEffect(() => {
    if (dates.length > 0 && sliderDateIndex >= dates.length) {
      setSliderDateIndex(dates.length - 1);
    }
  }, [dates.length, sliderDateIndex]);

  const allExcludedRoots = useMemo(
    () => [...excludedRoots, ...rootsExceedingRange],
    [excludedRoots, rootsExceedingRange]
  );

  // Whenever a root gets filtered out, switch the lower chart to stacked history.
  // Skipped until the IRR bounds settle so the initial load doesn't trigger a switch.
  useEffect(() => {
    if (!irrBoundsReady) {
      prevFilteredCountRef.current = allExcludedRoots.length;
      return;
    }
    const count = allExcludedRoots.length;
    if (count > prevFilteredCountRef.current) {
      setLowerChartMode("stacked");
    }
    prevFilteredCountRef.current = count;
  }, [allExcludedRoots, irrBoundsReady]);

  const activeDateIndex = dates.length > 0 ? Math.min(sliderDateIndex, dates.length - 1) : 0;
  const activeDate = dates[activeDateIndex] ?? "";

  const lastUpdateFreshness = useMemo(() => {
    if (!lastUpdate) return null;
    const ageMs = Date.now() - new Date(lastUpdate).getTime();
    const oneDay = 24 * 60 * 60 * 1000;
    const oneWeek = 7 * oneDay;
    if (ageMs > oneWeek) return "text-danger";
    if (ageMs > oneDay) return "text-warning";
    return "text-success";
  }, [lastUpdate]);

  const addExcludedRoot = useCallback(() => {
    const trimmed = excludeInput.trim();
    if (trimmed && !excludedRoots.includes(trimmed)) {
      const uppercaseTrimmed = trimmed.toUpperCase();
      if (roots.includes(uppercaseTrimmed)) {
        setExcludedRoots((prev) => [...prev, uppercaseTrimmed]);
      }
      setExcludeInput("");
    } else if (trimmed) {
      setExcludeInput("");
    }
  }, [excludeInput, excludedRoots, roots]);

  const removeExcludedRoot = useCallback((root: string) => {
    setExcludedRoots((prev) => prev.filter((r) => r !== root));
  }, []);

  const handleExcludeKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === " " || e.key === "," || e.key === "Enter") {
        e.preventDefault();
        addExcludedRoot();
      }
    },
    [addExcludedRoot]
  );

  const handleSliderEnd = useCallback((event: unknown) => {
    const idx = (event as { slider?: { active?: number } })?.slider?.active;
    if (typeof idx === "number") setSliderDateIndex(idx);
  }, []);

  return (
    <div
      className="w-100 h-100 p-4 px-5 d-flex flex-column gap-4 overflow-hidden"
      style={{ minHeight: 0, flex: "1 1 0" }}
    >
      <div
        className="flex-grow-1 d-flex flex-row justify-content-center gap-4 px-1 overflow-hidden"
        style={{ minHeight: 0, flex: "1 1 0" }}
      >
        <div className="card d-flex flex-column flex-grow-1 overflow-hidden " style={{ maxWidth: "45%", minHeight: 0 }}>
          <div
            className="card-header flex-shrink-0"
            style={{ display: "grid", gridTemplateColumns: "1fr auto 1fr", alignItems: "center", gap: "0.5rem" }}
          >
            <div />
            <h2 className="text-center fw-semibold ">Latest IRR Data</h2>
            <div className="form-check form-switch d-flex align-items-center gap-2 justify-content-end">
              <input
                className="form-check-input"
                type="checkbox"
                role="switch"
                id="sofrAdjusted"
                checked={sofrAdjusted}
                onChange={() => setSofrAdjusted(!sofrAdjusted)}
                style={{ marginTop: "2px", cursor: "pointer" }}
              />
              <label className="form-check-label" htmlFor="sofrAdjusted" style={{ cursor: "pointer" }}>
                SOFR adjusted IRR
              </label>
            </div>
          </div>
          <div className="card-body overflow-y-auto overflow-x-hidden flex-grow-1 p-0" style={{ minHeight: 0 }}>
            <div className="table-responsive h-100">
              <IRRLatestTable
                irrApiData={irrApiData}
                allExcludedRoots={allExcludedRoots}
                sofrAdjusted={sofrAdjusted}
              />
            </div>
          </div>
        </div>

        <div className="card d-flex flex-column flex-grow-1 overflow-hidden" style={{ width: "55%", maxWidth: "55%", minHeight: 0 }}>
          <div className="card-header flex-shrink-0"
            style={{ display: "grid", gridTemplateColumns: "1fr auto 1fr", alignItems: "center", gap: "0.5rem" }}
          >
            <div />
            <h2 className="text-center fw-semibold">Root IRR Time Series</h2>
            <div className="text-end d-flex align-items-center justify-content-end gap-1">
              {lastUpdateFreshness && (
                <span
                  className={`rounded-circle d-inline-block ${styles.freshnessIndicator} ${lastUpdateFreshness}`}
                  style={{
                    width: 8,
                    height: 8,
                    backgroundColor: "currentColor",
                    boxShadow: `0 0 6px currentColor`,
                    marginRight: 4,
                    marginTop: 2,
                  }}
                  title={
                    lastUpdateFreshness === "text-danger"
                      ? "Data is over a week old"
                      : lastUpdateFreshness === "text-warning"
                        ? "Data is over a day old"
                        : "Data is fresh"
                  }
                  aria-hidden
                />
              )}
              <span className="text-muted small">
                Data last updated: {lastUpdate ? new Date(lastUpdate).toLocaleString() : "N/A"}
              </span>
            </div>
          </div>

          <div className="card-body overflow-hidden flex-grow-1 d-flex flex-column gap-2" style={{ minHeight: 0 }}>
            <div className="w-100 card p-1 d-flex flex-column overflow-hidden" style={{ flex: "6 1 0", minHeight: 0 }}>
              <div className="d-flex gap-2 align-items-center ms-2 mb-2 mt-3 flex-shrink-0">
                <span>IRR Cutoff Filters:</span>
                <label className="text-nowrap d-inline mb-0 ms-2" htmlFor="min-irr-input">
                  Minimum IRR:
                </label>
                <input
                  id="min-irr-input"
                  type="number"
                  className="form-control d-inline w-auto"
                  value={minIrr}
                  onChange={(e) => setMinIrr(Number(e.target.value))}
                  min={-1}
                  max={1}
                  step={0.01}
                  style={{ display: "inline-block" }}
                />
                <label className="text-nowrap d-inline mb-0 ms-4" htmlFor="max-irr-input">
                  Maximum IRR:
                </label>
                <input
                  id="max-irr-input"
                  type="number"
                  className="form-control d-inline w-auto"
                  value={maxIrr}
                  onChange={(e) => setMaxIrr(Number(e.target.value))}
                  min={-1}
                  max={1}
                  step={0.01}
                  style={{ display: "inline-block" }}
                />
              </div>

              <div className="flex-grow-1 overflow-hidden position-relative" style={{ minHeight: 0 }}>
                <IRRBarChart
                  activeDate={activeDate}
                  allExcludedRoots={allExcludedRoots}
                  irrData={irrData}
                  roots={roots}
                  sofrAdjusted={sofrAdjusted}
                  dates={dates}
                  activeDateIndex={activeDateIndex}
                  onSliderEnd={handleSliderEnd}
                />
              </div>
              <div className="d-flex flex-column gap-2 mt-2 mb-2 mx-2 flex-shrink-0">
                <span>Enter Roots to exclude:</span>
                <div
                  className="form-control d-flex flex-wrap align-items-center gap-2 py-1 px-2"
                  style={{ minHeight: "38px" }}
                >
                  {excludedRoots.map((root) => (
                    <span
                      key={root}
                      role="button"
                      tabIndex={0}
                      className="badge bg-secondary text-white d-inline-flex align-items-center gap-1 flex-shrink-0"
                      style={{ cursor: "pointer", fontSize: "0.8rem" }}
                      onClick={() => removeExcludedRoot(root)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          removeExcludedRoot(root);
                        }
                      }}
                    >
                      {root}
                      <span aria-hidden="true">&times;</span>
                    </span>
                  ))}
                  <input
                    type="text"
                    className="border-0 flex-grow-1 py-0"
                    style={{ minWidth: "120px", outline: "none", background: "transparent" }}
                    value={excludeInput}
                    onChange={(e) => setExcludeInput(e.target.value)}
                    onKeyDown={handleExcludeKeyDown}
                    placeholder={
                      excludedRoots.length === 0
                        ? "Type root, then space, comma, or Enter"
                        : "Enter root to exclude"
                    }
                  />
                </div>
                <div
                  className="d-flex flex-wrap align-items-center gap-2 pe-none form-control opacity-50 bg-light border-dark-subtle"
                  style={{ minHeight: "38px" }}
                >
                  <span>Range Filtered Roots:</span>
                  {rootsExceedingRange.map((root) => (
                    <span
                      key={root}
                      className="badge bg-warning text-dark d-inline-flex align-items-center gap-1 flex-shrink-0"
                      style={{ fontSize: "0.8rem" }}
                    >
                      {root}
                    </span>
                  ))}
                </div>
              </div>
            </div>
            <div
              className="w-100 card p-1 d-flex flex-column overflow-hidden"
              style={{ flex: "4 1 0", minHeight: 0, containerType: "size", containerName: "irr-dropdown-card" }}
            >
              <div className="flex-grow-1 overflow-hidden position-relative" style={{ minHeight: 0 }}>
                <div className="position-absolute top-0 start-0 m-2 d-flex gap-2" style={{ zIndex: 10 }}>
                  <div className="btn-group" role="group" aria-label="IRR history chart mode">
                    <button
                      type="button"
                      className={`btn btn-sm btn-outline-secondary ${lowerChartMode === "stacked" ? "active" : ""}`}
                      onClick={() => setLowerChartMode("stacked")}
                    >
                      Stacked IRR
                    </button>
                    <button
                      type="button"
                      className={`btn btn-sm btn-outline-secondary ${lowerChartMode === "historical" ? "active" : ""}`}
                      onClick={() => setLowerChartMode("historical")}
                    >
                      Historical Root IRR
                    </button>
                  </div>
                  {lowerChartMode === "historical" ? (
                    <div className="dropdown">
                      <button
                        className="btn btn-sm btn-outline-secondary dropdown-toggle"
                        type="button"
                        id="irrDropdown"
                        data-bs-toggle="dropdown"
                        aria-expanded="false"
                      >
                        {selectedRolloverRoot ?? "Select a Rollover Root"}
                      </button>
                      <ul
                        className="dropdown-menu"
                        aria-labelledby="irrDropdown"
                        style={{ zIndex: 1050, maxHeight: "calc(100cqh - 50px)", overflowY: "auto" }}
                      >
                        {irrData.map((d) => (
                          <li key={d.root}>
                            <button
                              className="dropdown-item"
                              type="button"
                              onClick={() => setSelectedRolloverRoot(d.root)}
                            >
                              {d.root} <span className="text-muted small">({d.displayName})</span>
                            </button>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ) : (
                    <div className="form-check form-switch d-flex align-items-center gap-2 mb-0">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        role="switch"
                        id="stackedSofrAdjusted"
                        checked={stackedSofrAdjusted}
                        onChange={() => setStackedSofrAdjusted(!stackedSofrAdjusted)}
                        style={{ marginTop: "2px", cursor: "pointer" }}
                      />
                      <label
                        className="form-check-label text-nowrap small"
                        htmlFor="stackedSofrAdjusted"
                        style={{ cursor: "pointer" }}
                      >
                        SOFR adjusted IRR
                      </label>
                    </div>
                  )}
                </div>
                {lowerChartMode === "stacked" ? (
                  <IRRStackedChart
                    irrData={irrData}
                    allExcludedRoots={allExcludedRoots}
                    sofrAdjusted={stackedSofrAdjusted}
                  />
                ) : (
                  <IRRRolloverChart
                    selectedRolloverRoot={selectedRolloverRoot}
                    irrApiData={irrApiData}
                    sofrAdjusted={sofrAdjusted}
                  />
                )}

              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
